import React, { useState } from 'react';
import submissionWorkflowService, {
  Interview, InterviewStatus, InterviewType,
  INTERVIEW_TYPE_LABEL, INTERVIEW_STATUS_LABEL,
} from '../../../services/submissionWorkflow.service';
import { relativeZeit } from '../workflow/zeit';

/** Gespräche einer Submission: anlegen, abschließen, Feedback pflegen. */

interface Props {
  submissionId: string;
  interviews: Interview[];
  onGeaendert: () => void;
}

const feld: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: '0.82rem',
  borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)',
  background: 'var(--glass)', color: 'var(--text-primary)',
};
const label: React.CSSProperties = {
  display: 'block', fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: '3px',
};

function inEinerWoche(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(10, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const InterviewListe: React.FC<Props> = ({ submissionId, interviews, onGeaendert }) => {
  const [formularOffen, setFormularOffen] = useState(false);
  const [termin, setTermin] = useState(inEinerWoche());
  const [typ, setTyp] = useState<InterviewType>('VIDEO');
  const [dauer, setDauer] = useState('60');
  const [ort, setOrt] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [feedbackFuer, setFeedbackFuer] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');

  const anlegen = async () => {
    setFehler(null);
    try {
      setLaeuft(true);
      await submissionWorkflowService.interviewAnlegen(submissionId, {
        scheduled_at: new Date(termin).toISOString(),
        interview_type: typ,
        duration_minutes: dauer ? Number(dauer) : null,
        location: ort.trim() || null,
      });
      setFormularOffen(false);
      setOrt('');
      onGeaendert();
    } catch (e: any) {
      setFehler(e?.message || 'Das Gespräch konnte nicht angelegt werden.');
    } finally {
      setLaeuft(false);
    }
  };

  const statusSetzen = async (interview: Interview, status: InterviewStatus) => {
    setFehler(null);
    try {
      setLaeuft(true);
      await submissionWorkflowService.interviewStatus(interview.id, status,
        feedbackFuer === interview.id && feedback.trim() ? { client_feedback: feedback.trim() } : {});
      setFeedbackFuer(null);
      setFeedback('');
      onGeaendert();
    } catch (e: any) {
      setFehler(e?.message || 'Der Status konnte nicht geändert werden.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
          {interviews.length === 0 ? 'Keine Gespräche' : `${interviews.length} ${interviews.length === 1 ? 'Gespräch' : 'Gespräche'}`}
        </span>
        <button
          className="btn btn-ghost"
          style={{ marginLeft: 'auto', fontSize: '0.76rem', padding: '3px 8px' }}
          onClick={() => setFormularOffen(!formularOffen)}
          disabled={laeuft}
        >
          {formularOffen ? 'Abbrechen' : '+ Gespräch'}
        </button>
      </div>

      {formularOffen && (
        <div style={{
          padding: '10px', marginBottom: '10px', display: 'grid', gap: '8px',
          borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)', background: 'var(--glass)',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
            <div>
              <span style={label}>Termin</span>
              <input type="datetime-local" style={feld} value={termin} onChange={(e) => setTermin(e.target.value)} />
            </div>
            <div>
              <span style={label}>Art</span>
              <select style={feld} value={typ} onChange={(e) => setTyp(e.target.value as InterviewType)}>
                {(Object.keys(INTERVIEW_TYPE_LABEL) as InterviewType[]).map((t) => (
                  <option key={t} value={t}>{INTERVIEW_TYPE_LABEL[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <span style={label}>Dauer (Min)</span>
              <input type="number" min="5" step="15" style={feld} value={dauer} onChange={(e) => setDauer(e.target.value)} />
            </div>
          </div>
          <div>
            <span style={label}>Ort oder Link</span>
            <input style={feld} value={ort} onChange={(e) => setOrt(e.target.value)} placeholder="z. B. Teams-Link oder Adresse" />
          </div>
          <button className="btn btn-primary" style={{ fontSize: '0.82rem' }} disabled={laeuft} onClick={anlegen}>
            {laeuft ? 'Speichert…' : 'Gespräch anlegen'}
          </button>
        </div>
      )}

      {fehler && <div style={{ color: '#EF4444', fontSize: '0.8rem', marginBottom: '8px' }}>{fehler}</div>}

      <div style={{ display: 'grid', gap: '6px' }}>
        {interviews.map((i) => {
          const st = INTERVIEW_STATUS_LABEL[i.interview_status];
          const offen = i.interview_status === 'PLANNED' || i.interview_status === 'CONFIRMED';
          return (
            <div key={i.id} style={{
              padding: '8px 10px', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--glass-border)', background: 'var(--glass)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Runde {i.round_number}
                </span>
                <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                  {INTERVIEW_TYPE_LABEL[i.interview_type]} · {relativeZeit(i.starts_at)}
                  {i.duration_minutes ? ` · ${i.duration_minutes} Min` : ''}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 700, color: st.color }}>
                  {st.label}
                </span>
              </div>

              {(i.location || i.meeting_url) && (
                <div style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                  {i.meeting_url || i.location}
                </div>
              )}

              {i.client_feedback && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Rückmeldung: {i.client_feedback}
                </div>
              )}

              {offen && (
                <div style={{ marginTop: '6px' }}>
                  {feedbackFuer === i.id ? (
                    <div style={{ display: 'grid', gap: '6px' }}>
                      <textarea
                        style={{ ...feld, minHeight: '46px', resize: 'vertical' }}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Rückmeldung des Kunden (optional)"
                      />
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn btn-primary" style={{ fontSize: '0.74rem', padding: '3px 8px' }}
                          disabled={laeuft} onClick={() => statusSetzen(i, 'COMPLETED')}>
                          Durchgeführt
                        </button>
                        <button className="btn btn-ghost" style={{ fontSize: '0.74rem', padding: '3px 8px' }}
                          onClick={() => { setFeedbackFuer(null); setFeedback(''); }}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      <button className="btn btn-ghost" style={{ fontSize: '0.74rem', padding: '3px 8px' }}
                        disabled={laeuft} onClick={() => setFeedbackFuer(i.id)}>
                        Durchgeführt
                      </button>
                      {i.interview_status === 'PLANNED' && (
                        <button className="btn btn-ghost" style={{ fontSize: '0.74rem', padding: '3px 8px' }}
                          disabled={laeuft} onClick={() => statusSetzen(i, 'CONFIRMED')}>
                          Bestätigt
                        </button>
                      )}
                      <button className="btn btn-ghost" style={{ fontSize: '0.74rem', padding: '3px 8px', color: 'var(--text-tertiary)' }}
                        disabled={laeuft} onClick={() => statusSetzen(i, 'CANCELLED')}>
                        Abgesagt
                      </button>
                      <button className="btn btn-ghost" style={{ fontSize: '0.74rem', padding: '3px 8px', color: 'var(--text-tertiary)' }}
                        disabled={laeuft} onClick={() => statusSetzen(i, 'NO_SHOW')}>
                        Nicht erschienen
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InterviewListe;
